import React, { useState, useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, Filter, Search, Eye, X, Pencil } from 'lucide-react';
import AddEditLeaseholderModal from '../../../pages/Admin/Leaseholders/components/AddEditLeaseholderModal';
import EnhancedLeaseholdersTable from './EnhancedLeaseholdersTable';
import { TableColumn, ColumnWidth } from '../../../components/common/EnhancedTable/types';
import './EnhancedLeaseholders.css';
import { supabaseLeaseholderService } from '../../../services/supabase/leaseholders';
import type { Leaseholder, LeaseholderFilter } from '../../../types/leaseholder';
import { formatTextWithLinks } from '../../../utils/formatters';
import { usePermissions } from '../../../hooks/usePermissions';
import LeaseholderDetailsModal from '../../../components/Leaseholders/LeaseholderDetailsModal';

// Define the sort field type
type LeaseholderSortField = 'holder_id' | 'holder_name' | 'lot_count' | 'indicative_annual_value';

// Define the sort configuration type
interface SortConfig {
  field: LeaseholderSortField;
  direction: 'asc' | 'desc';
}

export default function EnhancedLeaseholders() {
  const queryClient = useQueryClient();
  const { canEditLeases, isAdmin, canManageSystemSettings } = usePermissions();
  
  // State for modals
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editLeaseholder, setEditLeaseholder] = useState<Leaseholder | null>(null);
  
  // State for filters, sorting, and pagination
  const [searchQuery, setSearchQuery] = useState('');
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [filters, setFilters] = useState<LeaseholderFilter>({
    position: null,
    name: null
  });
  const [sortConfig, setSortConfig] = useState<SortConfig>({
    field: 'holder_name',
    direction: 'asc'
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  
  // State for modals
  const [selectedLeaseholder, setSelectedLeaseholder] = useState<Leaseholder | null>(null);

  // Fetch all leaseholders
  const { data: leaseholders, isLoading, error } = useQuery({
    queryKey: ['leaseholders'],
    queryFn: supabaseLeaseholderService.getAll
  });

  // Extract unique names and positions for filter dropdowns
  const { positions, names } = useMemo(() => {
    const positionsSet = new Set<string>();
    const namesSet = new Set<string>();

    leaseholders?.forEach(leaseholder => {
      if (leaseholder.holder_position) positionsSet.add(leaseholder.holder_position);
      if (leaseholder.holder_name) namesSet.add(leaseholder.holder_name);
    });

    return {
      positions: Array.from(positionsSet).sort(),
      names: Array.from(namesSet).sort()
    };
  }, [leaseholders]);

  // Apply filters
  const filteredLeaseholders = useMemo(() => {
    if (!leaseholders) return [];

    return leaseholders.filter(leaseholder => {
      // Search query filter
      if (searchQuery) {
        const searchLower = searchQuery.toLowerCase();
        const matchesSearch = 
          leaseholder.holder_name.toLowerCase().includes(searchLower) ||
          (leaseholder.holder_position || '').toLowerCase().includes(searchLower) ||
          (leaseholder.holder_email || '').toLowerCase().includes(searchLower) ||
          (leaseholder.holder_abn?.toString() || '').includes(searchLower) ||
          (leaseholder.holder_acn?.toString() || '').includes(searchLower) ||
          (leaseholder.holder_indcn?.toString() || '').includes(searchLower) ||
          (leaseholder.holder_contact || '').toLowerCase().includes(searchLower) ||
          (leaseholder.holder_phone || '').toLowerCase().includes(searchLower);
        
        if (!matchesSearch) return false;
      }

      // Name filter (exact match from dropdown)
      if (filters.name && leaseholder.holder_name !== filters.name) {
        return false;
      }

      // Position filter
      if (filters.position && leaseholder.holder_position !== filters.position) {
        return false;
      }

      return true;
    });
  }, [leaseholders, searchQuery, filters]);

  // Apply sorting
  const sortedLeaseholders = useMemo(() => {
    if (!filteredLeaseholders) return [];
    
    return [...filteredLeaseholders].sort((a, b) => {
      const direction = sortConfig.direction === 'asc' ? 1 : -1;
      
      switch (sortConfig.field) {
        case 'holder_id':
          return direction * (a.holder_id - b.holder_id);
        case 'holder_name':
          return direction * a.holder_name.localeCompare(b.holder_name);
        case 'lot_count':
          return direction * ((a.lot_count || 0) - (b.lot_count || 0));
        case 'indicative_annual_value':
          return direction * ((a.indicative_annual_value || 0) - (b.indicative_annual_value || 0));
        default:
          return 0;
      }
    });
  }, [filteredLeaseholders, sortConfig]);

  // Apply pagination
  const paginatedLeaseholders = useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    return sortedLeaseholders.slice(startIndex, endIndex);
  }, [sortedLeaseholders, currentPage, pageSize]);

  // Handle sorting
  const handleSort = (sortConfig: { field: string; direction: 'asc' | 'desc' }) => {
    console.log('Sorting:', sortConfig);
    setSortConfig(sortConfig as SortConfig);
  };

  // Handle sort button clicks
  const handleSortButtonClick = (field: LeaseholderSortField) => {
    setSortConfig(current => ({
      field,
      direction: current.field === field && current.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  // Handle filter changes
  const handleFilterChange = (newFilters: Partial<LeaseholderFilter>) => {
    setFilters(current => ({ ...current, ...newFilters }));
  };

  // Handle edit leaseholder
  const handleEditClick = (leaseholder: Leaseholder) => {
    setEditLeaseholder(leaseholder);
    setIsEditModalOpen(true);
  };

  // Handle edit success
  const handleEditSuccess = () => {
    queryClient.invalidateQueries({ queryKey: ['leaseholders'] });
  };

  // Handle view details
  const handleViewDetails = (leaseholder: Leaseholder) => {
    setSelectedLeaseholder(leaseholder);
  };

  // Handle remove filter
  const handleRemoveFilter = (key: keyof LeaseholderFilter) => {
    setFilters(prev => ({ ...prev, [key]: null }));
  };

  // Handle pagination
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handlePageSizeChange = (size: number) => {
    setPageSize(size);
    setCurrentPage(1); // Reset to first page when changing page size
  };

  // Calculate pagination configuration
  const paginationConfig = {
    currentPage,
    pageSize,
    totalCount: sortedLeaseholders.length,
    onPageChange: handlePageChange,
    onPageSizeChange: handlePageSizeChange,
    pageSizeOptions: [25, 50, 100, 200]
  };

  // Define table columns
  const columns: TableColumn<Leaseholder>[] = [
    {
      id: 'edit',
      header: 'Edit',
      accessor: (row) => (
        <div className="action-buttons">
          <button
            onClick={() => handleEditClick(row)}
            className="action-button edit"
            title="Edit Leaseholder"
          >
            <Pencil className="h-5 w-5" />
          </button>
        </div>
      ),
      width: 'narrow' as ColumnWidth,
      visibleWhen: (permissions) => permissions.isAdmin || permissions.canManageSystemSettings
    },
    { 
      id: 'view', 
      header: 'View', 
      accessor: (row) => (
        <button
          onClick={() => handleViewDetails(row)}
          className="w-10 h-10 flex items-center justify-center text-primary hover:text-primary-dark bg-blue-50 hover:bg-blue-100 rounded-lg"
          title="View Details"
        >
          <Eye className="h-5 w-5" />
        </button>
      ),
      width: 'narrow' as ColumnWidth,
      align: 'center' as 'center'
    },
    {
      id: 'holder_name',
      header: 'Name',
      accessor: (row) => (
        <div className="truncate" title={row.holder_name}>
          {formatTextWithLinks(row.holder_name)}
        </div>
      ),
      width: 'wide' as ColumnWidth,
      sortable: true
    },
    {
      id: 'lot_count',
      header: 'No. of Lots',
      accessor: (row) => row.lot_count || 0,
      width: 'medium' as ColumnWidth,
      sortable: true
    },
    {
      id: 'indicative_annual_value',
      header: 'INDIC ANN.',
      accessor: (row) => row.indicative_annual_value 
        ? `$${row.indicative_annual_value.toLocaleString('en-US', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
          })}` 
        : '$0.00',
      width: 'medium' as ColumnWidth,
      align: 'right' as 'right',
      sortable: true
    },
    {
      id: 'holder_position',
      header: 'Position',
      accessor: (row) => row.holder_position || '-',
      width: 'medium' as ColumnWidth
    },
    {
      id: 'holder_contact',
      header: 'Contact Person',
      accessor: (row) => row.holder_contact || '-',
      width: 'medium' as ColumnWidth
    },
    {
      id: 'holder_phone',
      header: 'Phone',
      accessor: (row) => row.holder_phone || '-',
      width: 'medium' as ColumnWidth
    },
    {
      id: 'holder_email',
      header: 'Email',
      accessor: (row) => row.holder_email || '-',
      width: 'medium' as ColumnWidth
    },
    {
      id: 'holder_abn',
      header: 'ABN',
      accessor: (row) => row.holder_abn?.toString() || '-',
      width: 'medium' as ColumnWidth
    },
    {
      id: 'holder_acn',
      header: 'ACN',
      accessor: (row) => row.holder_acn?.toString() || '-',
      width: 'medium' as ColumnWidth
    },
    {
      id: 'holder_indcn',
      header: 'ICN',
      accessor: (row) => row.holder_indcn?.toString() || '-',
      width: 'medium' as ColumnWidth
    },
    {
      id: 'holder_id',
      header: 'ID',
      accessor: (row) => row.holder_id,
      width: 'narrow' as ColumnWidth,
      sortable: true
    }
  ];

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-800"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 text-red-500">
        Error loading leaseholder data. Please try again later.
      </div>
    );
  }

  return (
    <div className="enhanced-leaseholders-container p-4">
      <div className="enhanced-leaseholders-header">
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <h1 className="text-2xl font-bold">Leaseholders</h1>
            
            {/* Add button - only visible to users with admin privileges */}
            {(isAdmin || canManageSystemSettings) && (
              <button 
                onClick={() => setIsAddModalOpen(true)}
                className="flex items-center px-4 py-2 bg-primary text-white rounded-md hover:bg-primary-dark"
              >
                <Plus className="h-5 w-5 mr-2" />
                Add Leaseholder
              </button>
            )}
          </div>

          <div className="flex items-center gap-2">
            {/* Search input */}
            <div className="relative flex-1 max-w-md">
              <input
                type="text"
                placeholder="Search leaseholders..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-primary focus:border-primary"
              />
              <Search className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
            </div>
            
            {/* Filter button */}
            <button
              className="p-2 border border-gray-300 rounded-md hover:bg-gray-50 flex items-center gap-1"
              onClick={() => setIsFilterOpen(!isFilterOpen)}
              title="Toggle filters"
            >
              <Filter className="h-5 w-5 text-gray-500" />
              <span className="hidden sm:inline">Filters</span>
              {Object.values(filters).filter(Boolean).length > 0 && (
                <span className="ml-1 bg-primary text-white text-xs rounded-full px-2 py-0.5">
                  {Object.values(filters).filter(Boolean).length}
                </span>
              )}
            </button>
            
            <div className="text-base font-bold text-gray-700">
              Total Sub-Lease Holders: {sortedLeaseholders.length}
            </div>
          </div>

          {/* Active filters */}
          {Object.values(filters).some(Boolean) && (
            <div className="active-filters">
              {filters.name && (
                <div className="filter-tag">
                  <span className="filter-tag-label">Name:</span>
                  <span>{filters.name}</span>
                  <button
                    onClick={() => handleRemoveFilter('name')}
                    className="filter-tag-remove"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </div>
              )}
              {filters.position && (
                <div className="filter-tag">
                  <span className="filter-tag-label">Position:</span>
                  <span>{filters.position}</span>
                  <button
                    onClick={() => handleRemoveFilter('position')}
                    className="filter-tag-remove"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Filter panel */}
          {isFilterOpen && (
            <div className="filter-panel">
              <div className="filter-panel-header">
                <h3 className="font-medium text-gray-700">Filters</h3>
                <button
                  onClick={() => setFilters({
                    position: null,
                    name: null
                  })}
                  className="text-sm text-blue-600 hover:text-blue-800"
                >
                  Clear all
                </button>
              </div>
              <div className="filter-panel-body">
                {/* Name filter - dropdown */}
                <div className="filter-group">
                  <label htmlFor="name-filter">Name</label>
                  <select
                    id="name-filter"
                    value={filters.name || ''}
                    onChange={(e) => handleFilterChange({ name: e.target.value || null })}
                    className="border border-gray-300 rounded-md p-2 text-sm"
                  >
                    <option value="">All names</option>
                    {names.map(name => (
                      <option key={name} value={name}>{name}</option>
                    ))}
                  </select>
                </div>
                {/* Position filter */}
                <div className="filter-group">
                  <label htmlFor="position-filter">Position</label>
                  <select
                    id="position-filter"
                    value={filters.position || ''}
                    onChange={(e) => handleFilterChange({ position: e.target.value || null })}
                    className="border border-gray-300 rounded-md p-2 text-sm"
                  >
                    <option value="">All positions</option>
                    {positions.map(position => (
                      <option key={position} value={position}>{position}</option>
                    ))}
                  </select>
                </div>
                
              </div>
            </div>
          )}

          {/* Sort controls removed - now using column headers for sorting */}
        </div>
      </div>
      
      <div className="enhanced-leaseholders-table-container enhanced-table-force-scroll">
        <EnhancedLeaseholdersTable
          columns={columns}
          data={paginatedLeaseholders}
          keyField="holder_id"
          width="1800px"
          maxHeight="calc(100vh - 20rem)"
          loading={isLoading}
          emptyMessage="No leaseholders found"
          horizontalScroll={true}
          alternateRowColors={true}
          pagination={paginationConfig}
          defaultSort={sortConfig}
          onSort={handleSort}
          permissions={{
            isAdmin,
            canManageSystemSettings,
            canEditLeases
          }}
        />
      </div>
      
      {/* Leaseholder details modal */}
      {selectedLeaseholder && (
        <LeaseholderDetailsModal
          leaseholder={selectedLeaseholder}
          onClose={() => setSelectedLeaseholder(null)}
        />
      )}

      {/* Add Leaseholder Modal */}
      {isAddModalOpen && (
        <AddEditLeaseholderModal
          leaseholder={null}
          onClose={() => setIsAddModalOpen(false)}
          onSuccess={() => {
            handleEditSuccess();
            setIsAddModalOpen(false);
          }}
        />
      )}

      {/* Edit Leaseholder Modal */}
      {editLeaseholder && isEditModalOpen && (
        <AddEditLeaseholderModal
          leaseholder={editLeaseholder}
          onClose={() => setIsEditModalOpen(false)}
          onSuccess={() => {
            handleEditSuccess();
            setIsEditModalOpen(false);
          }}
        />
      )}
    </div>
  );
}
